import { computed, reactive, ref } from "vue";
import { useFormDraft } from "@/composables/useFormDraft";

export function useScrapAlmacenLaserForm() {
  const getToday = () => {
    const d = new Date();
    const year  = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day   = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Un id único por intento de envío: viaja con el payload como
  // "idempotency_key" para que el backend pueda reconocer un reintento
  // (botón doble-tap, recarga de página tras un error de red, etc.) y
  // devolver el registro ya creado en vez de duplicarlo.
  function generarIdempotencyKey() {
    if (typeof crypto !== "undefined" && crypto.randomUUID) {
      return crypto.randomUUID();
    }
    return `sal-${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }

  const initialState = {
    fecha: getToday(),
    operador: "",
    operadorLabel: "",
    producto: "",
    productoLabel: "",
    color: "",
    colorLabel: "", // ¿agrega
    colorHex: "", // agrega
    // null = el operador aún no responde "¿Lleva aditivo?". Sin valor por
    // defecto para que el dato siempre sea una elección explícita.
    aditivo: null,
    cantidad: "",
    codigosError: [], //cambia de string a array para multiselector
    folio: "",
    registeredAt: null,
  };

  const form = reactive({ ...initialState, idempotencyKey: generarIdempotencyKey() });

  const currentStep = ref(1);
  const { completeDraft, startNewDraft } = useFormDraft({
    key: "divisan:draft:scrap-almacen-laser",
    form,
    currentStep,
    // v2: aditivo pasó de false por defecto a null. Se descartan borradores
    // v1 para que no restauren un false que el operador nunca eligió.
    version: 2,
    maxStep: 4,
  });

  const canGoBack = computed(() => {
    return currentStep.value > 1 && currentStep.value < 5;
  });

  function parseNumber(value) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  function formatKg(value) {
    return parseNumber(value).toFixed(2);
  }

  function makeFolio() {
    const n = new Date();
    return `#SAL-${n.getFullYear()}-${Math.floor(1000 + Math.random() * 8999)}`;
  }

  function validateStep(step = currentStep.value) {
    if (step === 1) {
      return Boolean(form.fecha && form.operador);
    }
    if (step === 2) {
      return Boolean(form.producto && form.color) && typeof form.aditivo === "boolean";
    }
    if (step === 3) {
      // codigosError es array
      return (
        form.cantidad !== "" &&
        parseNumber(form.cantidad) >= 0 &&
        form.codigosError.length > 0
      );
    }
    return true;
  }

  function buildPayload() {
    return {
      tipo_reporte: "scrap_almacen_laser",
      fecha_registro: new Date().toISOString(),
      fecha: form.fecha,
      id_operador: form.operador,
      id_producto: form.producto,
      id_color: form.color,
      // Sin Boolean(): si llegara null, el backend lo rechaza en vez de
      // guardarlo silenciosamente como "sin aditivo".
      aditivo: form.aditivo,
      cantidad_scrap: parseNumber(form.cantidad), // piezas enteras
      codigos_defecto: form.codigosError.map((c) => (c.value)),
      idempotency_key: form.idempotencyKey,
    };
  }

  function next() {
    if (currentStep.value === 4) {
      form.folio = makeFolio();
      form.registeredAt = new Date();
    }
    if (currentStep.value < 5) {
      currentStep.value++;
    }
  }

  function back() {
    if (currentStep.value > 1) {
      currentStep.value--;
    }
  }

  function resetForm() {
    Object.assign(form, {
      ...initialState,
      fecha: getToday(),
      idempotencyKey: generarIdempotencyKey(),
    });
    currentStep.value = 1;
    startNewDraft();
  }

  return {
    form,
    currentStep,
    canGoBack,
    parseNumber,
    formatKg,
    validateStep,
    buildPayload,
    next,
    back,
    resetForm,
    completeDraft,
  };
}
